/**
 * 020 Conversation boot-intent resolution.
 *
 * The global conversation provider is LAZY by default. On each surface mount it
 * resolves what to do from an EXPLICIT signal — never from in-memory turn count
 * (the rejected `turnsCount` heuristic that mis-fired on cold navigation):
 *
 *   - `warm_continue`     — an LLM-driven cross-page continuation was handed into
 *                           THIS destination (`continuationRequested`). The ONLY
 *                           intent that dispatches an agent turn.
 *   - `hydrate_transcript`— a detailed conversation surface has a prior
 *                           conversation id; silently restore the transcript,
 *                           NO turn.
 *   - `lazy`              — wait for the user.
 *
 * Contract: backlog/specs/020-conversation-continuation/contracts/boot-intent.md
 */

import { ContentBlockSchema, type ContentBlock, type V2ConversationTurn } from '@shared/conversation-schema';

export type ConversationBootIntent = 'lazy' | 'hydrate_transcript' | 'warm_continue';

export interface BootIntentInput {
  /** Current route. */
  pathname: string;
  /** Active lifecycle conversation id, if any. */
  conversationId: string | null;
  /** Whether this surface shows a full transcript (the hydrate target). */
  isDetailedConversationSurface: boolean;
  /**
   * An explicit cross-page continuation handoff matched this destination
   * (set by the single-use queue in ConversationContext). The ONLY trigger for
   * `warm_continue` — deliberately NOT inferred from in-memory turn count.
   */
  continuationRequested: boolean;
}

export function resolveBootIntent(input: BootIntentInput): ConversationBootIntent {
  if (input.continuationRequested) return 'warm_continue';
  // Home is the lobby, not a transcript surface — never hydrate there (the
  // boot-intent contract: home never warms/hydrates unless an explicit
  // continuation was handed in, handled above).
  if (isHomeRoute(input.pathname)) return 'lazy';
  if (input.isDetailedConversationSurface && input.conversationId) return 'hydrate_transcript';
  return 'lazy';
}

function isHomeRoute(pathname: string): boolean {
  return pathname === '' || pathname === '/';
}

/**
 * 020 T024 — normalise server turn rows (from the V2 `/turns` read) into
 * `V2ConversationTurn[]` for a silent transcript hydrate. Hydrated turn ids are
 * NAMESPACED (`hydrate_*` fallback / kept server ids) so they are distinguishable
 * from runtime-minted ids — the live-block coalescer must never extend a
 * hydrated turn (D5). Malformed rows are dropped, never thrown.
 */
export function mapServerTurnsToV2(rows: unknown[]): V2ConversationTurn[] {
  return rows.flatMap((row, index) => {
    if (!isRecord(row)) return [];
    const role = normalizeRole(row.role);
    if (!role) return [];
    const content = normalizeContent(row);
    if (content.length === 0) return [];
    return [{
      turn_id: normalizeTurnId(row, index),
      role,
      content,
      timestamp: normalizeTimestamp(row.timestamp),
    }];
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeRole(role: unknown): V2ConversationTurn['role'] | null {
  if (role === 'user') return 'user';
  if (role === 'assistant' || role === 'agent') return 'assistant';
  return null;
}

/**
 * 004-p6 T018 — decode a `content`/`transcript` STRING that is actually a
 * JSON-encoded content-block ARRAY back into blocks. Assistant turns persist
 * `content: JSON.stringify(res.content)` (conversation-gateway), served that way
 * from the Redis stream AND rewritten onto `transcript` by the DB fallback — so a
 * scoping resume hydrate would otherwise collapse every widget (materialized
 * capture card) into a single JSON-text block (the mapServerTurnsToV2 Redis-shape
 * incident class, pre-mortem #8). Returns null when the string is not a block array
 * (plain text, a scalar, or a JSON array with no valid blocks — e.g. a user typing
 * "[1,2,3]") so the caller falls through to the text-block behaviour.
 */
function tryParseBlocksFromString(s: string): ContentBlock[] | null {
  const trimmed = s.trim();
  // Only an array literal can be a blocks payload; cheap guard before JSON.parse.
  if (!trimmed.startsWith('[')) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return null;
  }
  if (!Array.isArray(parsed)) return null;
  const blocks = parsed.flatMap((block) => {
    const r = ContentBlockSchema.safeParse(block);
    return r.success ? [r.data] : [];
  });
  return blocks.length > 0 ? blocks : null;
}

function normalizeContent(row: Record<string, unknown>): ContentBlock[] {
  if (Array.isArray(row.content)) {
    return row.content.flatMap((block) => {
      const parsed = ContentBlockSchema.safeParse(block);
      return parsed.success ? [parsed.data] : [];
    });
  }
  const contentStr = typeof row.content === 'string' ? row.content : null;
  const transcriptStr = typeof row.transcript === 'string' ? row.transcript : null;

  // Prefer decoding blocks from a JSON-string payload (widgets survive resume).
  const fromContent = contentStr ? tryParseBlocksFromString(contentStr) : null;
  if (fromContent) return fromContent;
  const fromTranscript = transcriptStr ? tryParseBlocksFromString(transcriptStr) : null;
  if (fromTranscript) return fromTranscript;

  // Text fallbacks. `transcript` first (DB-fallback branch rewrites the turn text
  // there); then the hot-path Redis `content` STRING (redis-memory.ts xadd) — without
  // it every Redis-sourced turn maps to empty content and drops, so an in-TTL "come
  // back to the call" resume silently no-ops.
  if (transcriptStr && transcriptStr.trim().length > 0) {
    return [{ type: 'text', text: transcriptStr }];
  }
  if (contentStr && contentStr.trim().length > 0) {
    return [{ type: 'text', text: contentStr }];
  }
  return [];
}

function normalizeTurnId(row: Record<string, unknown>, index: number): string {
  if (typeof row.turn_id === 'string' && row.turn_id.length > 0) return row.turn_id;
  if (typeof row.id === 'string' && row.id.length > 0) return row.id;
  const source =
    typeof row.stream_id === 'string' && row.stream_id.length > 0
      ? row.stream_id
      : typeof row.timestamp === 'string' && row.timestamp.length > 0
        ? row.timestamp
        : 'missing';
  return `hydrate_${index}_${source}`;
}

function normalizeTimestamp(timestamp: unknown): string {
  if (typeof timestamp === 'string' && !Number.isNaN(Date.parse(timestamp))) return timestamp;
  return new Date().toISOString();
}
